import { QueryFunction, useMutation, useQuery, useQueryClient } from 'react-query';
import {
	FacilityAccountViewListResponse,
	getAdminHomeLandingInvoicesMetersResponse,
	ApiError,
	getUtilityAcccounts,
	getUtilityCompanies,
	GetUtilityListModel,
	UtilityCompanyBody
} from 'src/modules/generated-api-clients/sql';
import { useApiSqlContext } from 'src/modules/contexts/ApiSqlContext';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { AxiosError } from 'axios';
import { QUERY_KEYS_SQL } from './queryKeyEnum';
import { QUERY_KEYS } from '../no-sql/enums';

export const useUtilityFacilitiesQuery = (organizationId: string = "") => {
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	return useQuery<FacilityAccountViewListResponse | undefined, ApiError>(
		['utilityFacilities', token, organizationId],
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (token === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
        let headers = {
					'id_token': token,
					'organization_id': organizationId
				}
				if (organizationId === "" || organizationId === undefined) {
					// throw new Error('Organization id is required');
					// console.log('no organization id - removing header organization_id');
					//@ts-ignore
					headers = { "id_token": token };
				} 
				// console.log('headers: ', headers);

				return await client.default.getAdminPortalHomeLandingFacilitiesAccountsPopulate(
					token,
					organizationId
				);
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else {
					console.error('Error fetching account information:', error);
					throw error;
				}
				return;
			}
		},
		{
			enabled: !!token// Ensure both organizationId and email are available
		}
	);
};

export const useUtilityInvoicesQuery = (accountId?: string) => {
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	return useQuery<getAdminHomeLandingInvoicesMetersResponse | undefined, ApiError>(
		['utilityInvoices', accountId, token],
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (token === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				if (accountId === undefined || accountId === '') {
					throw new Error('Account ID cannot be undefined');
				}
				const response = await client.default.getAdminPortalHomeLandingInvoicesMeters(
					accountId,
					token
				);
				return response;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else {
					console.error('Error fetching account information:', error);
					throw error;
				}
				return;
			}
		},
		{
			enabled: !!accountId && !!token // Ensure both account_id and email are available
		}
	);
};

export const useGetInvoiceAccounts = (utilityId?: string, facilityId?: string) => {
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	return useQuery<getUtilityAcccounts | undefined, ApiError>(
		['getAdminPortalUtilityAccountAccountsList', utilityId, facilityId, token],
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (token === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				if (utilityId === undefined || utilityId === '') {
					throw new Error('Utility ID cannot be undefined');
				}
				if (facilityId === undefined || facilityId === '') {
					throw new Error('Facility ID cannot be undefined');
				}
				const response = await client.default.getAdminPortalUtilityAccountAccountsList(
					token,
					utilityId,
					facilityId
				);
				return response;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					// Handle ApiError specifically
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else if (error instanceof AxiosError) {
					console.error(`AxiosError -> ${JSON.stringify(error)}`);
				} else {
					// Handle other errors
					console.error('Error:', error);
					throw error;
				}
				return;
			}
		},
		{
			enabled: !!utilityId && !!facilityId && !!token // Ensure both account_id and email are available
		}
	);
};

export const useUtilityCompaniesQuery = () => {
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator((context) => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
    const queryFn: QueryFunction<getUtilityCompanies, any> = async () => {
      try {
        if (!client) {
          throw new Error("API client is not initialized");
        }
        if (token === undefined) {
          throw new Error("AWS Amplify user attributes cannot be undefined");
        }
        
        const response = await client.default.getBackendGetUtilityCompanies();
        return response;
      } catch (error: unknown) {
        if (error instanceof ApiError) {
          console.error(
            `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
              error.message
            }\nBODY: ${JSON.stringify(error.body)}`
          );
          throw error;
        } else {
          console.error("Error fetching account information:", error);
          throw error;
        }
      }
    };
    return useQuery<getUtilityCompanies, ApiError>(
      ["getUtilityComponies", token],
      queryFn,
      {
        enabled: !!token,
      }
    );
};

export const useGetInvoiceAccountsByFacilityId = (facilityId?: string) => {
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	return useQuery<getUtilityAcccounts | undefined, ApiError>(
		['getAccountListByFacilityId', facilityId, token],
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (token === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				if (facilityId === undefined || facilityId === '') {
					throw new Error('Facility ID cannot be undefined');
				}

				const response = await client.default.getAdminPortalUtilityAccountAccountsList(
					token,
					facilityId
				);
				return response;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					// Handle ApiError specifically
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else if (error instanceof AxiosError) {
					console.error(`AxiosError -> ${JSON.stringify(error)}`);
				} else {
					// Handle other errors
					console.error('Error:', error);
					throw error;
				}
				return;
			}
		},
		{
			enabled: !!facilityId && !!token // Ensure both account_id and email are available
		}
	);
};

export const useListUtility = () => {
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	return useQuery<GetUtilityListModel | undefined, ApiError>(
		[QUERY_KEYS_SQL.utility.listUtility, token],
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (token === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				const response = await client.default.getAdminPortalUtilityPopulate(
					token
				);
				return response;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					// Handle ApiError specifically
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else if (error instanceof AxiosError) {
					console.error(`AxiosError -> ${JSON.stringify(error)}`);
				} else {
					// Handle other errors
					console.error('Error:', error);
					throw error;
				}
				return;
			}
		},
		{
			enabled: !!token // Ensure token is available
		}
	);
};

export const useReadUtility = (id: string) => {
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	return useQuery<GetUtilityListModel | undefined, ApiError>(
		[QUERY_KEYS_SQL.utility.readUtility, token, id],
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (token === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				if (id === undefined || id === '') {
					throw new Error('id is required');
				}
				return await client.default.getAdminPortalUtilityUtilityById(token, id);
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else {
					console.error('Error fetching account information:', error);
					throw error;
				}
				return;
			}
		},
		{
			enabled: !!token && !!id
		}
	);
};

export const useCreateUtility = () => {
	const queryClient = useQueryClient();
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	return useMutation<unknown, ApiError, UtilityCompanyBody>(
		async (requestBody) => {
			// Guard against errors
			if (!client) {
				throw new Error('API client is not initialized');
      }
			if (token === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			return client.default.postAdminPortalUtilityNewUtilityCompany(token, requestBody);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS_SQL.utility.listUtility);
				queryClient.invalidateQueries(QUERY_KEYS_SQL.utility.readUtility);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.SingleUtility);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					// Handle ApiError specifically
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else {
					// Handle other errors
					console.error('Error:', error);
				}
			}
		}
	);
};

export const useUpdateUtility = () => {
	const queryClient = useQueryClient();
	const { client } = useApiSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	if (!client) {
		throw new Error('API client is not initialized');
	}
	return useMutation<unknown, ApiError, UtilityCompanyBody>(
		(params: UtilityCompanyBody) => {
			if (!params) {
				throw new Error('Invalid Client draft.');
			}
			if (token === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			return client.default.putAdminPortalUtilityNewUtilityCompany(token, params);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS_SQL.utility.listUtility);
				queryClient.invalidateQueries(QUERY_KEYS_SQL.utility.readUtility);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.SingleUtility);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					// Handle ApiError specifically
					console.error(
						`ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
							error.message
						}\nBODY: ${JSON.stringify(error.body)}`
					);
				} else {
					// Handle other errors
					console.error('Error:', error);
				}
			}
		}
	);
};